/* eslint-disable @typescript-eslint/no-explicit-any */
import {UseFormReturn} from 'react-hook-form';
import Api from 'types/api';
import {toCamelCase} from 'utils/object';

/**
 * This method is used to narrow down the type of a response into a SuccessAxios.
 */
export const isSuccess = (data: any): data is Api.Response.SuccessAxios =>
    data?.type === 'success';

/**
 * This method is used to narrow down the type of a response into a RedirectAxios.
 */
export const isRedirect = (data: any): data is Api.Response.RedirectAxios =>
    data?.type === 'redirect';

/**
 * This method is used to narrow down the type of an error into an ApiErrorAxios.
 */
export const isApiErrorAxios = (
    error: Api.Response.ApiErrorAxios,
): error is Api.Response.ApiErrorAxios => {
    if (error.name !== 'AxiosError') return false;

    return error.response?.data?.response?.type === 'errors';
};

/**
 * Helper method to get the field errors and non field errors from an ApiErrorAxios.
 */
const getErrors = (
    methods: UseFormReturn<any>,
    errors: Record<string, [[string]]>,
): {
    fieldErrors: Record<string, string | undefined>;
    nonFieldErrors: string | undefined;
} => {
    const {getValues} = methods;
    const formKeys = Object.keys(getValues());
    const errorKeys = Object.keys(errors);

    const fieldErrorKeys = errorKeys.filter((key) => formKeys.includes(key));
    const nonFieldErrorKeys = errorKeys.filter(
        (key) => !formKeys.includes(key),
    );

    const fieldErrors: Record<string, string> = Object.fromEntries(
        fieldErrorKeys.map((key) => [key, errors[key].join(', ')]),
    );

    const nonFieldErrors = nonFieldErrorKeys
        .map((key) => errors[key])
        .join(' ,');

    return {
        fieldErrors,
        nonFieldErrors: nonFieldErrors === '' ? undefined : nonFieldErrors,
    };
};

export const onApiErrorAxios =
    (methods: UseFormReturn<any>) =>
    (error: Api.Response.ApiErrorAxios, ns?: string) => {
        const errors = toCamelCase(error.response?.data?.response?.errors);
        if (!errors) return;

        const {fieldErrors, nonFieldErrors} = getErrors(methods, errors);

        if (nonFieldErrors) {
            methods.setError('root.nonFieldErrors', {
                message: nonFieldErrors,
                type: 'error',
            });
        }

        Object.keys(fieldErrors).forEach((key) => {
            methods.setError(ns ? `${ns}.${key}` : key, {
                message: fieldErrors[key],
                type: 'error',
            });
        });
    };

export const onUnknownError =
    (methods: UseFormReturn<any>) => (error: Api.Response.Failure) => {
        methods.setError('root.nonFieldErrors', {
            message: error.message,
            type: 'error',
        });
    };

/**
 * This method is used in the `.catch(onApiError(methods))` statement of a `mutateAsync` call.
 *
 * It will try to corretly set any field errors, else it will set a nonFieldError with the `.message` value
 */
export const onApiError =
    (methods: UseFormReturn<any>, ns?: string) => (error: any) => {
        if (isApiErrorAxios(error)) {
            onApiErrorAxios(methods)(error, ns);
        } else {
            onUnknownError(methods)(error);
        }
    };

type Callbacks<R> = {
    onRedirect?: (response: OnlyRedirect<R>) => void;
    onSuccess?: (response: OnlySuccess<R>) => void;
};
type OnlyRedirect<R> = Extract<R, Api.Response.RedirectAxios<string>>;

type OnlySuccess<R> = Extract<R, Api.Response.SuccessAxios<unknown, unknown>>;

/**
 * - This method is used in the `.then(onApiSuccess({...}))` statement of a `mutateAsync` call.
 *
 * - It will call the appropriate callback based on the response type.
 *
 * - If a response type is not handled, it will throw an error.
 */
export const onApiSuccess =
    <R>({onRedirect, onSuccess}: Callbacks<R>) =>
    (response: R) => {
        if (isSuccess(response) && onSuccess) {
            return onSuccess(response as OnlySuccess<R>);
        }

        if (onRedirect && isRedirect(response)) {
            return onRedirect(response as OnlyRedirect<R>);
        }

        throw new Error('Unhandled response type');
    };
